import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, MessageCircle, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_CONTACT } from '@/config/brand';
import { trackWhatsAppClick } from '@/lib/crm/track';
import { MENU_SECTIONS, BEST_SELLERS, TONE_BG, type MenuLink } from './menuData';
import { useMenuPrices, trackMenuClick } from './useMenuPrices';

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenSearch: () => void;
  favoritesHref: string;
}

export const MobileMenuDrawer = ({ open, onClose, onOpenSearch, favoritesHref }: Props) => {
  const [expanded, setExpanded] = useState<string | null>('loja');
  const price = useMenuPrices();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const go = (item: string, href: string) => { trackMenuClick(item, href, 'mobile'); onClose(); };

  const LinkRow = ({ link, section, dot }: { link: MenuLink; section: string; dot?: boolean }) => (
    <Link to={link.href} onClick={() => go(`${section}:${link.title}`, link.href)} className="flex min-h-12 items-center gap-3 rounded-lg px-3 py-2 active:bg-v2-bg">
      {dot && link.tone && <span className={cn('h-3 w-3 shrink-0 rounded-full', TONE_BG[link.tone])} aria-hidden />}
      <span>
        <span className="block text-base font-semibold text-foreground leading-snug">{link.title}</span>
        {link.desc && <span className="block text-sm text-v2-muted leading-snug">{link.desc}</span>}
      </span>
    </Link>
  );

  return (
    <div className={cn('min-[980px]:hidden fixed inset-0 z-[60]', open ? 'pointer-events-auto' : 'pointer-events-none')} aria-hidden={!open}>
      <div className={cn('absolute inset-0 bg-v2-ink/50 transition-opacity', open ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={cn(
          'absolute left-0 top-0 h-full w-[92vw] max-w-[420px] bg-background shadow-2xl flex flex-col transition-transform duration-300',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center gap-2 border-b border-border p-3">
          <button
            type="button"
            onClick={() => { onClose(); onOpenSearch(); }}
            className="flex min-h-12 flex-1 items-center gap-2 rounded-full bg-v2-bg px-4 text-base text-v2-muted"
          >
            <Search className="h-5 w-5" aria-hidden /> Buscar produtos
          </button>
          <button type="button" onClick={onClose} aria-label="Fechar menu" className="flex h-12 w-12 items-center justify-center rounded-full">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <section className="pt-4">
            <p className="px-4 text-xs font-semibold uppercase tracking-wider text-v2-muted">Mais vendidos</p>
            <div className="mt-2 flex gap-3 overflow-x-auto px-4 pb-2 snap-x">
              {BEST_SELLERS.map((p) => (
                <Link
                  key={p.handle}
                  to={`/produto/${p.handle}`}
                  onClick={() => go(`mais_vendidos:${p.short}`, `/produto/${p.handle}`)}
                  className="w-[132px] shrink-0 snap-start overflow-hidden rounded-xl border border-border/60"
                >
                  <div className={cn('h-1', TONE_BG[p.tone])} aria-hidden />
                  <img src={p.image} alt="" width={132} height={132} loading="lazy" decoding="async" className="aspect-square w-full object-cover bg-white" />
                  <div className="p-2">
                    <p className="text-sm font-semibold leading-snug line-clamp-2">{p.short}</p>
                    {price(p.handle) && <p className="text-sm text-v2-muted">{price(p.handle)}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <nav aria-label="Menu principal" className="mt-2 px-2">
            {MENU_SECTIONS.map((s) => {
              const isOpen = expanded === s.id;
              return (
                <div key={s.id} className="border-b border-border/70">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : s.id)}
                    aria-expanded={isOpen}
                    className="flex min-h-14 w-full items-center justify-between px-3 text-lg font-semibold"
                  >
                    {s.label}
                    <ChevronDown className={cn('h-5 w-5 transition-transform', isOpen && 'rotate-180')} aria-hidden />
                  </button>
                  {isOpen && (
                    <div className="pb-3">
                      {s.columns.map((c) => (
                        <div key={c.heading} className="mb-2">
                          <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-v2-muted">{c.heading}</p>
                          {c.links.map((l) => <LinkRow key={l.href + l.title} link={l} section={s.label} dot={c.grid} />)}
                          {c.footerLink && <LinkRow link={c.footerLink} section={s.label} />}
                        </div>
                      ))}
                      {s.id === 'loja' && (
                        <Link to="/quiz" onClick={() => go('Loja:quiz', '/quiz')} className="flex min-h-12 items-center px-3 text-base font-semibold text-primary">
                          Não sabe qual? Responda 3 perguntas →
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="p-4">
            <a
              href={`https://wa.me/${BRAND_CONTACT.whatsappInternational}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { trackWhatsAppClick('mobile_menu', BRAND_CONTACT.whatsappInternational); trackMenuClick('whatsapp', 'wa.me', 'mobile'); }}
              className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-v2-whatsapp px-5 text-base font-semibold text-background"
            >
              <MessageCircle className="h-5 w-5" aria-hidden /> Tirar dúvida no WhatsApp
            </a>
            <p className="mt-4 flex flex-wrap items-center justify-center gap-x-2 text-sm text-v2-muted">
              <Link to="/perfil" onClick={() => go('conta', '/perfil')} className="inline-flex min-h-12 items-center px-1">Minha conta</Link>·
              <Link to={favoritesHref} onClick={() => go('favoritos', favoritesHref)} className="inline-flex min-h-12 items-center px-1">Favoritos</Link>·
              <Link to="/pedidos" onClick={() => go('pedidos', '/pedidos')} className="inline-flex min-h-12 items-center px-1">Pedidos</Link>
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
};
